// Lead-sheet chord-symbol typesetting — the ONE parser shared by every view
// (the chart score, the guitar sheet and the notation staff), so the same
// chord reads identically everywhere. A printed symbol is a big ROOT (letter
// + accidental + the minor/half-dim/dim marker), the extensions RAISED small
// ("Em7♭5" → E m + superscript 7♭5), and a slash bass back at full size.
// Pure string → segments so width maths and rendering can never disagree.

export interface ChordLabelParts {
  /** Full-size head: root letter, its accidental, and the minor/ø/° marker. */
  root: string;
  /** Raised, smaller tail: extensions/alterations ("7♭5", "9", "♭9", "13"). */
  sup: string;
  /** Full-size slash bass ("/D"), empty when none. */
  bass: string;
  /** Full-size outer group parentheses, separate from quality parentheses. */
  prefix?: string;
  suffix?: string;
}

/**
 * How chord qualities are spelled. The tune's identity is unchanged — this
 * only picks the display dialect, consistently across every view:
 *  - `triangle`: maj7 → ∆, minor → m, half-dim → ø, dim → °  (jazz shorthand)
 *  - `minus`:    maj7 → ∆, minor → −, half-dim → ø, dim → °  (Real Book dash)
 *  - `plain`:    maj7 → maj7, minor → m, half-dim → m7♭5, dim → dim
 */
export type ChordSymbolStyle = 'triangle' | 'minus' | 'plain';

export const CHORD_SYMBOL_STYLES: { id: ChordSymbolStyle; label: string; sample: string }[] = [
  { id: 'triangle', label: 'Symbols', sample: 'C∆ · Dm7 · Bø7' },
  { id: 'minus', label: 'Real Book', sample: 'C∆ · D−7 · Bø7' },
  { id: 'plain', label: 'Spelled', sample: 'Cmaj7 · Dm7 · Bm7♭5' },
];

/** Width of one full-size chord char in staff-spaces (~2.1-space font). */
const FULL_CHAR = 1.38;
/** Width of one superscript char (the raised tail draws at ~0.67×). */
const SUP_CHAR = 0.95;
/** Extra width of a full-size accidental over FULL_CHAR: a rendered root
 *  "♭"/"♯" measures ~2.1 spaces (browser-measured on Body And Soul — the
 *  flat estimate left E♭m7-class symbols ~0.6 spaces short, and adjacent
 *  symbols visibly abutted). Superscript accidentals measure true. */
const ACC_EXTRA = 0.7;

/** Width of a full-size segment, accidentals paying their real width. */
function fullWidth(txt: string): number {
  return txt.length * FULL_CHAR + (txt.match(/[♭♯♮]/g)?.length ?? 0) * ACC_EXTRA;
}

/**
 * Split a display chord symbol into typeset segments in the chosen dialect.
 * Symbols that don't look like a chord (N.C., staff cues) come back all-root
 * so they print unchanged at full size.
 */
export function parseChordLabel(text: string, style: ChordSymbolStyle = 'triangle'): ChordLabelParts {
  const prefix = text.startsWith('(') ? '(' : '';
  let chord = prefix ? text.slice(1) : text;
  // A quality such as m(maj7) already balances its own parentheses. Only an
  // unmatched final close belongs to the surrounding optional-chord group.
  const balance = [...chord].reduce((sum, char) => sum + (char === '(' ? 1 : char === ')' ? -1 : 0), 0);
  const suffix = chord.endsWith(')') && balance < 0 ? ')' : '';
  if (suffix) chord = chord.slice(0, -1);
  const m = /^([A-G][♭♯#b]*)(.*)$/.exec(chord);
  if (!m || text.startsWith('N.C')) return { root: text, sup: '', bass: '' };
  const rootLetter = m[1];
  let rest = m[2];

  // Slash bass returns to the baseline at full size — split it off first.
  let bass = '';
  const slash = rest.lastIndexOf('/');
  if (slash >= 0 && /^[A-G]/.test(rest.slice(slash + 1))) {
    bass = rest.slice(slash);
    rest = rest.slice(0, slash);
  }

  let rootMark = ''; // the quality glyph that rides full-size next to the root
  const minor = style === 'minus' ? '−' : 'm';

  // Half-diminished (m7♭5 ⇄ ø): the whole marker, before the plain minor rule.
  // iReal writes it as h, which its display turns into ø ("Bø7"); a bare
  // extension after the ø ("Bø9") is the m7♭5(9) the catalogue spells, so
  // every source of the same chord reads alike in each dialect.
  const halfSpelled = /^(?:m|-|−|min)7?(?:♭|b)5(.*)$/.exec(rest);
  const halfGlyph = /^[øh]7?(.*)$/.exec(rest);
  const half =
    halfSpelled?.[1] ??
    (halfGlyph && (/^[0-9]/.test(halfGlyph[1]) ? `(${halfGlyph[1]})` : halfGlyph[1]));
  const dim = /^(?:dim|°|o)(7)?(.*)$/.exec(rest);
  if (half != null) {
    if (style === 'plain') {
      rootMark = minor;
      rest = `7♭5${half}`;
    } else {
      rootMark = 'ø';
      rest = `7${half}`;
    }
  } else if (dim) {
    rootMark = style === 'plain' ? '' : '°';
    rest = (style === 'plain' ? 'dim' : '') + (dim[1] ?? '') + dim[2];
  } else {
    // Pure minor marker (m / min / -), never "maj".
    if (/^(?:m(?!aj)(?!a\b)|min(?!or)?)/.test(rest)) {
      rootMark = minor;
      rest = rest.replace(/^(?:min|m)/, '');
    } else if (rest.startsWith('-') || rest.startsWith('−')) {
      rootMark = minor;
      rest = rest.slice(1);
    }
    // Major 7: ∆ (dropping the 7) for the symbol dialects, "maj7" for plain.
    if (style === 'plain') {
      rest = rest.replace(/^∆7?(?![0-9])/, 'maj7').replace(/^∆(?=[0-9])/, 'maj');
    } else {
      rest = rest.replace(/^(?:maj|ma|∆|M)7(?![0-9])/, '∆').replace(/^(?:maj|ma|∆|M)(?=[0-9]|$)/, '∆');
    }
  }

  // The 6/9 chord prints with its slash, as the books write it: iReal's "69"
  // (which exports must keep) read as sixty-nine. Never a slash bass here.
  rest = rest.replace(/^69(?![0-9])/, '6/9');

  return {
    root: rootLetter + rootMark, sup: rest, bass,
    ...(prefix ? { prefix } : {}),
    ...(suffix ? { suffix } : {}),
  };
}

/** The symbol's printed width in staff-spaces (raised tail counts smaller). */
export function chordLabelSpaces(text: string, style: ChordSymbolStyle = 'triangle'): number {
  const p = parseChordLabel(text, style);
  return fullWidth((p.prefix ?? '') + p.root + (p.suffix ?? '')) + p.sup.length * SUP_CHAR + fullWidth(p.bass);
}
